/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@mui/material';
import styled from 'styled-components';
import cx from 'classnames';
import END_POINT from '../../config/endpoints.constants';
import withoutAuth from '../../hoc/withoutAuth';
import AnonymousLayout from '../AnonymousLayout';
import SEO from '../common/SEO';
import TextField from '../common/TextField';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { getErrorMessage } from '../../lib/error-handling';
import regionConfig from '../../config';
import Recaptcha from 'react-google-recaptcha';

const StyledForgotPassword = styled.div`
    .forgot-password-card {
        width: 450px;
    }
    .email-sent-card {
        width: 500px;
    }
    .heading {
        font-size: 32px;
        font-weight: 700;
    }
    .instructions {
        font-size: 16px;
        font-weight: 600;
        color: var(--black-60, rgba(17, 17, 17, 0.6));
    }
    .forgot-password-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: center;
    }
    @media (max-width: 768px) {
        height: auto;
        margin-bottom: 105px;
    }
`;

// Native width of the reCAPTCHA "normal" widget (in px). Used to compute the
// scale factor needed to stretch it to the surrounding form width.
const RECAPTCHA_NATIVE_WIDTH = 304;
const RECAPTCHA_NATIVE_HEIGHT = 78;

const ForgotPassword = () => {
    const toast = useToast();
    const [isEmailSent, setEmailSent] = useState(false);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const captcha = useRef<any>(null);
    const navigate = useNavigate();
    const { register, handleSubmit } = useForm();

    const onsubmit = async (data: any) => {
        if (!captchaVerified) {
            toast.error('Please verify that you are a human');

            return;
        }

        // eslint-disable-next-line no-param-reassign
        data.applicationName = 'customer-portal';
        data.recaptchaToken = captchaVerified;

        await axios
            .post(END_POINT.FORGOTPWD, data, {
                headers: {
                    'App-Name': 'userapp',
                    'App-Source': 'userapp',
                },
            })
            .then((res: any) => {
                if (res.data.code === 0) {
                    setEmailSent(true);
                }
            })
            .catch((err) => {
                toast.error(getErrorMessage(err));
            })
            .finally(() => {
                captcha.current?.reset();
                setCaptchaVerified(false);
            });
    };

    const handleCaptchaVerified = (response: any) => {
        if (response) {
            setCaptchaVerified(response);
        } else {
            setCaptchaVerified(false);
        }
    };

    return (
        <AnonymousLayout isLoginScreen={true}>
            <SEO
                title="Forget Password"
                description={`${regionConfig.appName} Admin Forget Password`}
            />
            <StyledForgotPassword className="w-full h-auto mt-32 flex justify-center items-center">
                <Card
                    className={cx('forgot-password-card card mb-6', {
                        'email-sent-card': isEmailSent,
                    })}
                >
                    <CardContent className="p-4 w-full">
                        {!isEmailSent && (
                            <div>
                                <div className=" mb-8" style={{}}>
                                    <div className="heading">Get a password link</div>
                                    <div className="instructions">
                                        Enter your email and we’ll send you a link to create or
                                        reset your password.
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit(onsubmit)}>
                                    <div className="forgot-password-form">
                                        <TextField
                                            name="email"
                                            placeholder="Enter your email"
                                            type="email"
                                            inputRef={register}
                                            fullWidth
                                            aria-required="true"
                                            style={{ width: '100%' }}
                                        />

                                        <Recaptcha
                                            ref={captcha}
                                            sitekey={regionConfig.captchaSiteKey}
                                            render="explicit"
                                            onChange={handleCaptchaVerified}
                                        />

                                        <Button
                                            type="submit"
                                            fullWidth
                                            size="large"
                                            aria-label="Submit"
                                            className="primary-bg-color normal-case source-sans-pro-font"
                                        >
                                            Send password link
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        )}
                        {isEmailSent && (
                            <div>
                                <div className="mb-8">
                                    <div className="heading">Check your email</div>
                                    <div className="instructions">
                                        If the account exists, we sent a password link to your
                                        email. Please check your inbox or spam folder and follow the
                                        instructions.
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    onClick={() => navigate('/login', { replace: true })}
                                    fullWidth
                                    size="large"
                                    aria-label="Go To Login"
                                    className="primary-bg-color normal-case source-sans-pro-font"
                                >
                                    Go back to Login
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </StyledForgotPassword>
        </AnonymousLayout>
    );
};

export default withoutAuth(ForgotPassword);
